// src/settings/Company.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Alert,
    Switch,
    FormControlLabel,
    Avatar,
    InputAdornment,
    Tooltip
} from '@mui/material';
import {
    Delete,
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    CheckCircle,
    Cancel,
    Business,
    LocationOn,
    Phone,
    Email,
    Language,
    Receipt,
    QrCode,
    AttachMoney,
    AccessTime,
    CloudUpload,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APICompanyStore,
    APICompanyList,
    APICompanyUpdate,
    APICompanyDelete,
    APICompanyNames
} from '../../apiServices/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../apiServices/Text';
import {
    formatCreatedAtDate,
    formatNumberWithComma,
    getStatusById,
    getUserDetails,
    recordsPerPage,
    statusData
} from '../../utils/helpers';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utils/Permission';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';

// Transition component for dialogs
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Company = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        code: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        receipt_footer: '',
        registration_no: '',
        tax_id: '',
        currency: 'USD',
        timezone: 'UTC',
        is_active: '1'
    });

    const [filters, setFilters] = useState({
        name: '',
        code: '',
        is_active: '',
        dateFrom: '',
        dateTo: ''
    });

    const [errors, setErrors] = useState({
        name: false,
        code: false,
        address: false,
        phone: false,
        email: false,
        website: false,
        is_active: false
    });

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);

    // Status options
    const statusOptions = [
        { id: '1', name: 'Active' },
        { id: '0', name: 'Inactive' }
    ];

    // Currency options
    const currencyOptions = [
        { id: 'USD', name: 'USD - US Dollar' },
        { id: 'EUR', name: 'EUR - Euro' },
        { id: 'GBP', name: 'GBP - British Pound' },
        { id: 'UGX', name: 'UGX - Ugandan Shilling' },
        { id: 'KES', name: 'KES - Kenyan Shilling' },
        { id: 'TZS', name: 'TZS - Tanzanian Shilling' }
    ];

    // Timezone options
    const timezoneOptions = [
        { id: 'UTC', name: 'UTC' },
        { id: 'Africa/Nairobi', name: 'East Africa Time (EAT)' },
        { id: 'Africa/Johannesburg', name: 'South Africa Standard Time' },
        { id: 'America/New_York', name: 'Eastern Time' },
        { id: 'America/Los_Angeles', name: 'Pacific Time' },
        { id: 'Europe/London', name: 'London' },
        { id: 'Asia/Dubai', name: 'Dubai' }
    ];

    // Use the updated permission hook
    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
    }, [showToast]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APICompanyList);

            if (result.success) {
                const companies = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: companies
                }));
                showToast("Success", TEXT_DATA_LOADED, "success");
            } else {
                setError(result.message);
            }
        } catch (error) {
            let errorMessage = TEXT_CONNECTION_ERROR;
            setError(errorMessage);
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Get status display text
    const getStatusText = (status) => {
        return status === '1' || status === 1 ? 'Active' : 'Inactive';
    };

    // Get status color
    const getStatusColor = (status) => {
        return status === '1' || status === 1 ? 'success' : 'error';
    };

    // Get status icon
    const getStatusIcon = (status) => {
        return status === '1' || status === 1 ? <CheckCircle /> : <Cancel />;
    };

    // Handle logo change
    const handleLogoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file size (2MB limit)
            if (file.size > 2 * 1024 * 1024) {
                showToast("Error", "Logo size must not exceed 2MB", "error");
                return;
            }
            // Validate file type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!validTypes.includes(file.type)) {
                showToast("Error", "Please select a valid image file (JPG, PNG, GIF)", "error");
                return;
            }
            
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle remove logo
    const handleRemoveLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
        // Clear the file input
        const fileInput = document.getElementById('logo-upload-input');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Filter based on search term and filters
    const filteredData = pageState.pageData.filter(record => {
        const matchesSearch = searchTerm === '' ||
            record.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            getStatusText(record.is_active).toLowerCase().includes(searchTerm.toLowerCase());

        const matchesName = !filters.name ||
            record.name?.toLowerCase().includes(filters.name.toLowerCase());

        const matchesCode = !filters.code ||
            record.code?.toLowerCase().includes(filters.code.toLowerCase());

        const matchesStatus = !filters.is_active ||
            record.is_active?.toString() === filters.is_active;

        let matchesDate = true;
        if (filters.dateFrom && record.created_at) {
            const recordDate = new Date(record.created_at).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate >= filterFromDate;
        }

        if (filters.dateTo && record.created_at) {
            const recordDate = new Date(record.created_at).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate <= filterToDate;
        }

        return matchesSearch && matchesName && matchesCode && matchesStatus && matchesDate;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        // Check permission for add
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add companies", "error");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            name: '',
            code: '',
            address: '',
            phone: '',
            email: '',
            website: '',
            receipt_footer: '',
            registration_no: '',
            tax_id: '',
            currency: 'USD',
            timezone: 'UTC',
            is_active: '1'
        });
        setLogoPreview(null);
        setLogoFile(null);
        // Reset file input
        const fileInput = document.getElementById('logo-upload-input');
        if (fileInput) {
            fileInput.value = '';
        }
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleCloseView = () => setOpenView(false);

    const handleOpenEdit = (record) => {
        // Check permission for edit
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit companies", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        setFormData({
            name: record.name || '',
            code: record.code || '',
            address: record.address || '',
            phone: record.phone || '',
            email: record.email || '',
            website: record.website || '',
            receipt_footer: record.receipt_footer || '',
            registration_no: record.registration_no || '',
            tax_id: record.tax_id || '',
            currency: record.currency || 'USD',
            timezone: record.timezone || 'UTC',
            is_active: record.is_active?.toString() || '1'
        });
        setLogoPreview(record.logo_url);
        setLogoFile(null);
        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        // Check permission for delete
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete companies", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            name: '',
            code: '',
            is_active: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const validateForm = () => {
        const errorsList = [];
        if (!formData.name?.trim()) errorsList.push('Company name is required');
        if (!formData.code?.trim()) errorsList.push('Company code is required');

        const codeRegex = /^[A-Za-z0-9]+$/;
        if (formData.code && !codeRegex.test(formData.code)) {
            errorsList.push('Company code must contain only letters and numbers');
        }

        if (formData.email && formData.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                errorsList.push('Please enter a valid email address');
            }
        }

        if (formData.phone && formData.phone.trim()) {
            const phoneRegex = /^[+]?[0-9\s\-\(\)]{10,}$/;
            if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
                errorsList.push('Please enter a valid phone number');
            }
        }

        if (errorsList.length > 0) {
            showToast("Validation Error", errorsList.join('\n'), "warning");
            return false;
        }
        return true;
    };

    const handleDialogClose = () => {
        setOpenAdd(false);
        setOpenView(false);
        setOpenDelete(false);
        setFilterOptions(false);
    };

    const handleFormDataReset = () => {
        setFormData({
            name: '',
            code: '',
            address: '',
            phone: '',
            email: '',
            website: '',
            receipt_footer: '',
            registration_no: '',
            tax_id: '',
            currency: 'USD',
            timezone: 'UTC',
            is_active: '1'
        });
        setLogoPreview(null);
        setLogoFile(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // For ADD/UPDATE operations, check permissions
        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add companies", "error");
            return;
        }
        
        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update companies", "error");
            return;
        }
        
        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete companies", "error");
            return;
        }
        
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        if (postStatus !== "DELETE") {
            formDataToSend.append('name', formData.name);
            formDataToSend.append('code', formData.code);
            formDataToSend.append('is_active', formData.is_active);
            if (formData.address) formDataToSend.append('address', formData.address);
            if (formData.phone) formDataToSend.append('phone', formData.phone);
            if (formData.email) formDataToSend.append('email', formData.email);
            if (formData.website) formDataToSend.append('website', formData.website);
            if (formData.receipt_footer) formDataToSend.append('receipt_footer', formData.receipt_footer);
            if (formData.registration_no) formDataToSend.append('registration_no', formData.registration_no);
            if (formData.tax_id) formDataToSend.append('tax_id', formData.tax_id);
            if (formData.currency) formDataToSend.append('currency', formData.currency);
            if (formData.timezone) formDataToSend.append('timezone', formData.timezone);
            if (logoFile) {
                formDataToSend.append('logo', logoFile);
            }
        }

        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;

            if (postStatus === "ADD") {
                result = await apiService.create(APICompanyStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APICompanyUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APICompanyDelete, formDataToSend);
            }

            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleFormDataReset();
                handleDialogClose();
                fetchData();
            }
        } catch (error) {
            console.error("API Error:", error);
            showToast("Error", error.response?.data?.message || "Operation failed", "error");
        }
    };

    // Show loading while permissions are being loaded
    if (permissionLoading) {
        return <div>Loading permissions...</div>;
    }

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Company Management ( {formatNumberWithComma(pageState.pageData.length)} Total )
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Add Company
                            </Button>
                        )}
                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handlefilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                View Options
                            </Button>
                        )}
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search companies..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.name || filters.code || filters.is_active || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.name && (
                            <Chip
                                label={`Name: ${filters.name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.code && (
                            <Chip
                                label={`Code: ${filters.code}`}
                                onDelete={() => setFilters(prev => ({ ...prev, code: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.is_active && (
                            <Chip
                                label={`Status: ${getStatusText(filters.is_active)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, is_active: '' }))}
                                color={getStatusColor(filters.is_active)}
                                variant="outlined"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Button size="small" onClick={clearFilters} startIcon={<CloseIcon />} sx={{ ml: 1 }}>
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Currency</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((company, index) => (
                                    <TableRow
                                        key={company.id || index}
                                        hover
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Chip label={page * rowsPerPage + index + 1} color="primary" size="small" />
                                        </TableCell>
                                        <TableCell>{formatCreatedAtDate(company.created_at)}</TableCell>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Business sx={{ color: 'primary.main' }} />
                                                {company.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={company.code} color="secondary" variant="outlined" size="small" />
                                        </TableCell>
                                        <TableCell>
                                            {company.phone ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    {company.phone}
                                                </Box>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {company.email ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: '150px' }}>
                                                        {company.email}
                                                    </Typography>
                                                </Box>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={company.currency} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getStatusById(company.is_active)}
                                                size="small"
                                                color={company.is_active === 1 ? "primary" : "error"}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {hasPermission('VIEW') && (
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenView(company)}
                                                        color="primary"
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {hasPermission('EDIT') && (
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenEdit(company)}
                                                        color="secondary"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {hasPermission('DELETE') && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpenDelete(company)}
                                                        color="error"
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, color: 'text.secondary' }}>
                            <Search sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No companies found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Companies
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Companies
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Name / Code
                        </Typography>

                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                        />

                        <TextField
                            fullWidth
                            label="Code"
                            name="code"
                            value={filters.code}
                            onChange={handleFilterChange}
                        />

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Status
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="is_active"
                                value={filters.is_active}
                                onChange={handleFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {statusOptions.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Box>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseOptions}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleCloseOptions}
                        color="primary"
                        variant="contained"
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openAdd}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') handleCloseAdd();
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{postStatus === "ADD" ? "Add Company" : "Edit Company"}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            {/* Basic Information */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    Basic Information
                                </Typography>
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Company Name *"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    error={errors.name}
                                    helperText={errors.name ? "Company name is required" : ""}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Company Code *"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleInputChange}
                                    required
                                    error={errors.code}
                                    helperText={errors.code ? "Unique company code is required" : ""}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    error={errors.phone}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    error={errors.email}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Website"
                                    name="website"
                                    value={formData.website}
                                    onChange={handleInputChange}
                                    error={errors.website}
                                    placeholder="https://www.example.com"
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        name="is_active"
                                        value={formData.is_active}
                                        onChange={handleInputChange}
                                        label="Status"
                                    >
                                        {statusData.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>
                            {/* Registration Information */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 2 }}>
                                    Registration Information
                                </Typography>
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Registration Number"
                                    name="registration_no"
                                    value={formData.registration_no}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Tax ID / VAT Number"
                                    name="tax_id"
                                    value={formData.tax_id}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <br/>
                            {/* Financial Settings */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 2 }}>
                                    Financial Settings
                                </Typography>
                            </Grid>
                            <br/>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Currency</InputLabel>
                                    <Select
                                        name="currency"
                                        value={formData.currency}
                                        onChange={handleInputChange}
                                        label="Currency"
                                    >
                                        {currencyOptions.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Timezone</InputLabel>
                                    <Select
                                        name="timezone"
                                        value={formData.timezone}
                                        onChange={handleInputChange}
                                        label="Timezone"
                                    >
                                        {timezoneOptions.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>

                            {/* Logo Upload */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 2 }}>
                                    Company Logo
                                </Typography>
                            </Grid>
                            <br/>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {logoPreview && (
                                        <Box sx={{ position: 'relative' }}>
                                            <Avatar
                                                src={logoPreview}
                                                sx={{ width: 100, height: 100, borderRadius: 2 }}
                                                variant="rounded"
                                            />
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    top: -8,
                                                    right: -8,
                                                    bgcolor: 'error.main',
                                                    color: 'white',
                                                    '&:hover': { bgcolor: 'error.dark' }
                                                }}
                                                onClick={handleRemoveLogo}
                                            >
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Box>
                                    )}
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<CloudUpload />}
                                    >
                                        {logoPreview ? 'Change Logo' : 'Upload Logo'}
                                        <input
                                            id="logo-upload-input"
                                            type="file"
                                            hidden
                                            accept="image/jpeg,image/jpg,image/png,image/gif"
                                            onChange={handleLogoChange}
                                        />
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                        Max size: 2MB. Supported: JPG, PNG, GIF
                                    </Typography>
                                </Box>
                            </Grid>
                            <br/>
                            {/* Receipt Footer */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 2 }}>
                                    Receipt Settings
                                </Typography>
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Receipt Footer Message"
                                    name="receipt_footer"
                                    value={formData.receipt_footer}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={3}
                                    placeholder="Thank you for choosing us. We wish you good health!"
                                />
                            </Grid>
                        </Box>

                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button color="error" onClick={handleCloseAdd} variant="contained">
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" variant="contained">
                                {postStatus === "ADD" ? "Add Company" : "Update Company"}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') handleCloseView();
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Company Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                {selectedRecord.logo_url && (
                                    <Avatar
                                        src={selectedRecord.logo_url}
                                        sx={{ width: 100, height: 100, borderRadius: 2 }}
                                        variant="rounded"
                                    />
                                )}
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Business sx={{ color: 'primary.main' }} />
                                        <b>Name:</b> {selectedRecord.name}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Code:</b> {selectedRecord.code}</Typography>
                                </Grid>

                                {selectedRecord.address && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <LocationOn sx={{ color: 'text.secondary', mt: 0.5 }} />
                                            <span><b>Address:</b> {selectedRecord.address}</span>
                                        </Typography>
                                    </Grid>
                                )}

                                {selectedRecord.phone && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Phone sx={{ color: 'text.secondary' }} />
                                            <b>Phone:</b> {selectedRecord.phone}
                                        </Typography>
                                    </Grid>
                                )}

                                {selectedRecord.email && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Email sx={{ color: 'text.secondary' }} />
                                            <b>Email:</b> {selectedRecord.email}
                                        </Typography>
                                    </Grid>
                                )}

                                {selectedRecord.website && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Language sx={{ color: 'text.secondary' }} />
                                            <b>Website:</b> {selectedRecord.website}
                                        </Typography>
                                    </Grid>
                                )}

                                {selectedRecord.registration_no && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <QrCode sx={{ color: 'text.secondary' }} />
                                            <b>Registration No:</b> {selectedRecord.registration_no}
                                        </Typography>
                                    </Grid>
                                )}

                                {selectedRecord.tax_id && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Receipt sx={{ color: 'text.secondary' }} />
                                            <b>Tax ID:</b> {selectedRecord.tax_id}
                                        </Typography>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AttachMoney sx={{ color: 'text.secondary' }} />
                                        <b>Currency:</b> {selectedRecord.currency}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AccessTime sx={{ color: 'text.secondary' }} />
                                        <b>Timezone:</b> {selectedRecord.timezone}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="subtitle2">
                                        <b>Status:</b>
                                        <Chip
                                            icon={getStatusIcon(selectedRecord.is_active)}
                                            label={getStatusById(selectedRecord.is_active)}
                                            color={getStatusColor(selectedRecord.is_active)}
                                            size="small"
                                            sx={{ ml: 1 }}
                                        />
                                    </Typography>
                                </Grid>

                                {selectedRecord.receipt_footer && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Receipt sx={{ color: 'text.secondary' }} />
                                            <span><b>Receipt Footer:</b> {selectedRecord.receipt_footer}</span>
                                        </Typography>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Created Date:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Last Updated:</b> {formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') handleCloseDelete();
                }}
                TransitionComponent={Transition}
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Company</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this company?
                    </Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {selectedRecord.name} <br />
                            <Typography variant="body2" color="text.secondary">
                                Code: {selectedRecord.code}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Chip
                                    icon={<Business />}
                                    label={selectedRecord.name}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                <Chip
                                    icon={getStatusIcon(selectedRecord.is_active)}
                                    label={getStatusText(selectedRecord.is_active)}
                                    color={getStatusColor(selectedRecord.is_active)}
                                    size="small"
                                />
                            </Box>
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseDelete} variant="contained">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Company;